import { useMemo, useState } from "react";
import { useSnackbar } from "notistack";

import {
  Bucket,
  BucketAsset,
  BucketHistoryFailure,
  BucketNumberEnum,
  useAddBucketAssetMutation,
  useAddHeldAssetsMutation,
  useCreateBucketMutation,
  useDeleteBucketMutation,
  useEnsureBucketHistoryMutation,
  useRemoveBucketAssetMutation,
  useSyncBucketMutation,
  useUpdateBucketInceptionDateMutation,
  useUpdateBucketAssetMutation,
} from "features/trading/buckets";
import { HeldPeriod } from "features/trading/heldPeriods";
import { NOTISTACK_FAILURE_OPTIONS } from "constants/index";

export interface BucketActions {
  // The inception date is picked BEFORE the bucket exists — see
  // BucketCreateButton — so it travels in the create request rather than a
  // PATCH behind it. Required: cancelling the prompt aborts the creation,
  // so there is no "created without one" path through the UI.
  create: (inceptionDate: string) => Promise<boolean>;
  removeBucket: () => Promise<boolean>;
  // `null` clears the date back to "not set".
  setInceptionDate: (inceptionDate: string | null) => Promise<boolean>;
  addAsset: (assetId: number) => Promise<boolean>;
  removeAsset: (member: BucketAsset) => Promise<boolean>;
  toggleMain: (member: BucketAsset) => Promise<boolean>;
  setIncluded: (member: BucketAsset, included: boolean) => Promise<boolean>;
  sync: () => Promise<boolean>;
  addAllHoldings: () => Promise<boolean>;
  ensureHistory: () => Promise<boolean>;
  isCreating: boolean;
  isDeleting: boolean;
  isSettingInceptionDate: boolean;
  isAddingAsset: boolean;
  isRemovingAsset: boolean;
  isSyncing: boolean;
  isAddingHeld: boolean;
  isEnsuringHistory: boolean;
}

/**
 * All bucket mutations with shared error/success handling — the single
 * source of bucket behavior for the client-page Buckets tab and the
 * report-settings bucket components. Every action resolves to a success
 * flag so callers can close modals / clear inputs.
 *
 * `heldPeriods` are the report's reporting periods, and they decide which
 * holdings `addAllHoldings` adds: the ones held ACROSS them, or — absent,
 * as on the client page's Buckets tab, which has no reporting period —
 * the ones the account holds now. Passed in rather than read from storage,
 * for the same reason the asset picker's are: these components are shared
 * with a screen that must NOT inherit a period it never chose.
 *
 * The returned object is memoized: its identity only changes when the
 * bucket, account, periods or a loading flag change — never on unrelated
 * renders — so memoized member rows stay stable while e.g. typing in the
 * search.
 */
export const useBucketActions = (
  accountId: number | null | undefined,
  number: BucketNumberEnum,
  bucket: Bucket | undefined,
  heldPeriods?: HeldPeriod[],
): BucketActions => {
  const { enqueueSnackbar } = useSnackbar();

  const [createBucket, { isLoading: isCreating }] = useCreateBucketMutation();
  const [deleteBucket, { isLoading: isDeleting }] = useDeleteBucketMutation();
  const [addBucketAsset, { isLoading: isAddingAsset }] =
    useAddBucketAssetMutation();
  const [removeBucketAsset, { isLoading: isRemovingAsset }] =
    useRemoveBucketAssetMutation();
  const [updateBucketAsset] = useUpdateBucketAssetMutation();
  const [syncBucket, { isLoading: isSyncing }] = useSyncBucketMutation();
  const [
    updateBucketInceptionDate,
    { isLoading: isSettingInceptionDate },
  ] = useUpdateBucketInceptionDateMutation();
  const [addHeldAssets, { isLoading: isAddingHeld }] =
    useAddHeldAssetsMutation();
  const [ensureBucketHistory] = useEnsureBucketHistoryMutation();
  // Generation runs as a LOOP of batched requests (a Bucket 3 can hold
  // ~900 assets) — the mutation's own isLoading flickers between batches,
  // so the busy flag covers the whole loop instead.
  const [isEnsuringHistory, setIsEnsuringHistory] = useState(false);

  const bucketId = bucket?.id;

  return useMemo<BucketActions>(() => {
    const run = async (
      action: () => Promise<unknown>,
      failureMessage: string,
    ): Promise<boolean> => {
      try {
        await action();
        return true;
      } catch {
        enqueueSnackbar(failureMessage, NOTISTACK_FAILURE_OPTIONS);
        return false;
      }
    };

    const notifyAdded = (added: number, noneMessage: string) =>
      enqueueSnackbar(added ? `${added} asset(s) added.` : noneMessage, {
        variant: "success",
      });

    const notifyHistoryFailures = (failed: BucketHistoryFailure[]) => {
      if (!failed.length) return;
      enqueueSnackbar(
        `History generation failed for ${failed.length} asset(s) — see the statuses in the list.`,
        { variant: "warning" },
      );
    };

    const notifyHistoryPending = (pending: number) => {
      if (!pending) return;
      enqueueSnackbar(
        `History generation for ${pending} asset(s) was deferred — click "Generate histories" to run it.`,
        { variant: "info" },
      );
    };

    const withBucket = (
      action: (bucketId: number) => Promise<unknown>,
      failureMessage: string,
    ): Promise<boolean> => {
      if (!bucketId) return Promise.resolve(false);
      return run(() => action(bucketId), failureMessage);
    };

    return {
      create: (inceptionDate) => {
        if (!accountId) return Promise.resolve(false);
        return run(
          () =>
            createBucket({
              account: accountId,
              number,
              inception_date: inceptionDate,
            }).unwrap(),
          "Couldn't create the bucket.",
        );
      },
      removeBucket: () =>
        withBucket(
          (id) => deleteBucket({ id }).unwrap(),
          "Couldn't delete the bucket.",
        ),
      setInceptionDate: (inceptionDate) =>
        withBucket(async (id) => {
          await updateBucketInceptionDate({
            bucketId: id,
            inception_date: inceptionDate,
          }).unwrap();
          enqueueSnackbar(
            inceptionDate
              ? "Inception date saved."
              : "Inception date cleared.",
            { variant: "success" },
          );
        }, "Couldn't save the inception date."),
      addAsset: (assetId) =>
        withBucket(async (id) => {
          const member = await addBucketAsset({ bucketId: id, assetId }).unwrap();
          if (member.warning) {
            enqueueSnackbar(member.warning, { variant: "warning" });
          }
        }, "Couldn't add the asset to the bucket."),
      removeAsset: (member) =>
        withBucket(
          (id) =>
            removeBucketAsset({ bucketId: id, assetId: member.asset }).unwrap(),
          "Couldn't remove the asset from the bucket.",
        ),
      toggleMain: (member) =>
        withBucket(
          (id) =>
            updateBucketAsset({
              bucketId: id,
              assetId: member.asset,
              is_main: !member.is_main,
            }).unwrap(),
          "Couldn't update the main asset.",
        ),
      setIncluded: (member, included) =>
        withBucket(
          (id) =>
            updateBucketAsset({
              bucketId: id,
              assetId: member.asset,
              included,
            }).unwrap(),
          "Couldn't update the asset.",
        ),
      sync: () =>
        // Bucket 3 only adds/removes members — no history generation to
        // report (its report values are calculated dynamically).
        withBucket(async (id) => {
          const { added } = await syncBucket({ bucketId: id }).unwrap();
          notifyAdded(added, "Sync finished: no new assets.");
        }, "Couldn't sync the bucket."),
      addAllHoldings: () =>
        withBucket(async (id) => {
          const { added, history_failed, history_pending } =
            await addHeldAssets({ bucketId: id, heldPeriods }).unwrap();
          notifyAdded(added, "All holdings are already in the bucket.");
          notifyHistoryFailures(history_failed);
          notifyHistoryPending(history_pending);
        }, "Couldn't add the account's holdings."),
      ensureHistory: () =>
        withBucket(async (id) => {
          // Batched loop: each request generates a slice of the incomplete
          // members (staying under request timeouts on ~900-asset buckets)
          // and returns a cursor. Failed members keep their stored error;
          // the cursor moves past them, so the loop always terminates.
          setIsEnsuringHistory(true);
          try {
            let processed = 0;
            let failedCount = 0;
            let afterId: number | undefined;
            for (;;) {
              const batch = await ensureBucketHistory({
                bucketId: id,
                afterId,
              }).unwrap();
              processed += batch.processed;
              failedCount += batch.failed.length;
              if (!batch.remaining || batch.next_after_id === null) break;
              afterId = batch.next_after_id;
            }
            enqueueSnackbar(
              processed
                ? `Histories generated for ${processed - failedCount} of ${processed} asset(s).`
                : "All assets already have account history.",
              { variant: failedCount ? "warning" : "success" },
            );
          } finally {
            setIsEnsuringHistory(false);
          }
        }, "Couldn't generate account histories."),
      isCreating,
      isDeleting,
      isSettingInceptionDate,
      isAddingAsset,
      isRemovingAsset,
      isSyncing,
      isAddingHeld,
      isEnsuringHistory,
    };
  }, [
    accountId,
    number,
    bucketId,
    heldPeriods,
    enqueueSnackbar,
    createBucket,
    deleteBucket,
    updateBucketInceptionDate,
    addBucketAsset,
    removeBucketAsset,
    updateBucketAsset,
    syncBucket,
    addHeldAssets,
    ensureBucketHistory,
    isCreating,
    isDeleting,
    isSettingInceptionDate,
    isAddingAsset,
    isRemovingAsset,
    isSyncing,
    isAddingHeld,
    isEnsuringHistory,
  ]);
};
